import React, { useState } from 'react';
import { PageLayout } from '../../../ui/layout/PageLayout';

import { usePostExternalRecuperoPassword } from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import {
  FormHandler,
  FormItem,
  SubmitButton,
  TextInput,
} from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { LinkButton, useBreadcrumbItem, useHistory } from '@framework/ui';
import { yup } from '@framework/validation';
import { Card, Result, message } from 'antd';
import Paragraph from 'antd/es/typography/Paragraph';
import Title from 'antd/es/typography/Title';
import { down } from 'styled-breakpoints';
import styled from 'styled-components';

const StyledCard = styled(Card)`
  width: 550px;
  margin-inline: auto;
  ${down('md')} {
    min-width: 450px;
  }
  ${down('sm')} {
    min-width: unset;
    width: calc(100vw - 40px);
    > .ant-card-body {
      padding: 24px 12px;
    }
  }
`;

const ForgotPasswordSchema = yup
  .object({
    email: yup.string().email().required().label('Email'),
  })
  .required()
  .noUnknown();

export interface IForgotPasswordPagePage {}

export function ForgotPasswordPage(props: IForgotPasswordPagePage) {
  const history = useHistory();
  const { t } = useTranslation();

  useBreadcrumbItem({
    path: '/login',
    title: 'Login',
  });

  useBreadcrumbItem({
    path: history.location.pathname,
    title: t('user.forgot-password.breadcrumb', 'Reimposta password'),
  });

  const handleRecoverPassword = usePostExternalRecuperoPassword();

  const [success, setSuccess] = useState(false);

  if (success) {
    return (
      <PageLayout>
        <div style={{ paddingBottom: '50px' }}>
          <Card>
            <Result
              status="success"
              title={t(
                'user.password-reset-request.response.success.title',
                'Richiesta modifica password inviata',
              )}
              subTitle={t(
                'user.password-reset-request.response.success.subtitle',
                'Controlla la tua casella e-mail. Ti abbiamo inviato un’email con le istruzioni per reimpostare la password.',
              )}
              extra={
                <LinkButton
                  type="primary"
                  to="/login"
                  children={t(
                    'user.password-reset-request.response.success.button-login',
                    'Torna al Login',
                  )}
                />
              }
            />
          </Card>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div style={{ paddingBottom: '50px' }}>
        <StyledCard>
          <FormHandler
            schema={ForgotPasswordSchema}
            onSubmit={async value => {
              try {
                await handleRecoverPassword.mutateAsync({ data: value });
                setSuccess(true);
              } catch (error) {
                console.error(error);
                message.error(getNetworkErrorMessage(error, t('user.forgot-password.api-error-generic', 'Si è verificato un errore durante il recupero della password. Riprovare'))); // prettier-ignore
              }
            }}
            hideVisualFeedback
          >
            <Title level={3}>
              {t('user.forgot-password.title', 'Hai dimenticato la password?')}
            </Title>
            <Paragraph>
              {t(
                'user.forgot-password.text',
                `Inserisci l’email con cui ti sei registrato. Riceverai via email il link per reimpostare la password.`,
              )}
            </Paragraph>

            <FormItem label="Email" name="email">
              <TextInput name="email" placeholder="Email" />
            </FormItem>
            <SubmitButton
              type="primary"
              style={{ width: '100%' }}
              pristineMessage={null}
              icon={null}
            >
              {t('user.forgot-password.button-text', `Inviami un'email`)}
            </SubmitButton>
          </FormHandler>
        </StyledCard>
      </div>
    </PageLayout>
  );
}
